/**
 * Q2 hex-floor pulses — brief tile brighten on weapon fire.
 *
 * When a side fires, the tile under the shooter briefly glows. Reads
 * like the floor took the shock of the shot. Each pulse is keyed by
 * `(shooter, tick)` so re-mounting at the same tick doesn't double-
 * spawn. Lifetime is 120ms — short enough that scrubbing back leaves
 * at worst a fraction of a second of stale glow.
 */

import { useEffect, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import type { Mesh } from "three";
import type { BattleState } from "@pilot-protocol/replay-parser";
import { hexToWorld, TILE_THICKNESS } from "./hexMath";
import type { RenderEvent } from "../replay/events";

export interface FloorPulsesProps {
  eventsHere: ReadonlyArray<RenderEvent>;
  state: BattleState;
  tick: number;
  colorA?: string;
  colorB?: string;
}

interface ActivePulse {
  id: string;
  position: [number, number, number];
  color: string;
  startedAt: number;
}

export const FLOOR_PULSE_DURATION_MS = 120;
const FLOOR_PULSE_RADIUS = 0.85;
const FLOOR_PULSE_BASE_OPACITY = 0.55;
const FLOOR_OFFSET = TILE_THICKNESS + 0.015;
const DEFAULT_COLOR_A = "#7dd3fc";
const DEFAULT_COLOR_B = "#fdba74";

export function FloorPulses({
  eventsHere,
  state,
  tick,
  colorA = DEFAULT_COLOR_A,
  colorB = DEFAULT_COLOR_B,
}: FloorPulsesProps) {
  const [pulses, setPulses] = useState<ActivePulse[]>([]);
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const now = performance.now();
    const fresh: ActivePulse[] = [];
    let fireIdx = 0;
    for (const e of eventsHere) {
      if (e.kind !== "weapon_fire" && e.kind !== "weapon_miss") continue;
      const id = `floor-${e.tick}-${e.shooter}-${fireIdx}`;
      fireIdx++;
      if (seenRef.current.has(id)) continue;
      seenRef.current.add(id);
      const shooterMech = e.shooter === "A" ? state.mech_a : state.mech_b;
      const w = hexToWorld(shooterMech.position, 0);
      fresh.push({
        id,
        position: [w.x, FLOOR_OFFSET, w.z],
        color: e.shooter === "A" ? colorA : colorB,
        startedAt: now,
      });
    }
    if (fresh.length > 0) setPulses((p) => [...p, ...fresh]);
  }, [eventsHere, state, tick, colorA, colorB]);

  useFrame(() => {
    if (pulses.length === 0) return;
    const now = performance.now();
    const live = pulses.filter((p) => now - p.startedAt < FLOOR_PULSE_DURATION_MS);
    if (live.length !== pulses.length) setPulses(live);
  });

  if (pulses.length === 0) return null;

  return (
    <group name="floor-pulses">
      {pulses.map((p) => (
        <PulseTile key={p.id} pulse={p} />
      ))}
    </group>
  );
}

interface PulseTileProps {
  pulse: ActivePulse;
}

function PulseTile({ pulse }: PulseTileProps) {
  const meshRef = useRef<Mesh>(null);
  useFrame(() => {
    const m = meshRef.current;
    if (!m) return;
    const elapsed = performance.now() - pulse.startedAt;
    const t = Math.max(0, Math.min(1, elapsed / FLOOR_PULSE_DURATION_MS));
    // Quick triangle: brighten to peak, fade out.
    const tri = t < 0.4 ? t / 0.4 : (1 - t) / 0.6;
    const mat = (m.material as { opacity?: number; transparent?: boolean });
    if (typeof mat.opacity === "number") {
      mat.opacity = FLOOR_PULSE_BASE_OPACITY * Math.max(0, tri);
      mat.transparent = true;
    }
  });
  return (
    <mesh ref={meshRef} position={pulse.position} rotation={[-Math.PI / 2, 0, 0]}>
      <circleGeometry args={[FLOOR_PULSE_RADIUS, 6]} />
      <meshBasicMaterial
        color={pulse.color}
        transparent
        opacity={FLOOR_PULSE_BASE_OPACITY}
        depthWrite={false}
      />
    </mesh>
  );
}
